import React, { useState, useEffect } from "react";
import {
  Trash2,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
  Edit2,
} from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import Loader from "../../components/common/Loader";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

const NewsAndBlog = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [currentEvent, setCurrentEvent] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET;
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;

  const fetchEvents = async () => {
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_API_URL}/news/get-all`,
        headers: {},
      };
      const startTime = Date.now();
      const response = await axios.request(config);
      if (response?.data) {
        setEvents(response?.data?.data);
      }
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);
      setTimeout(() => setIsLoading(false), remaining);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleDelete = (eventId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        let config = {
          method: "delete",
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_API_URL}/news/delete/${eventId}`,
          headers: {},
        };
        axios
          .request(config)
          .then((response) => {
            console.log("Deleted:", response.data);
            Swal.fire({
              title: "Deleted!",
              text: "The event has been deleted successfully.",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            fetchEvents();
          })
          .catch((error) => {
            console.error("Delete error:", error);
            Swal.fire({
              title: "Error!",
              text: "Something went wrong while deleting the event.",
              icon: "error",
            });
          });
      } else {
        Swal.fire({
          title: "Cancelled",
          text: "Your event is safe 😊",
          icon: "info",
          timer: 1200,
          showConfirmButton: false,
        });
      }
    });
  };

  const handleAddEvent = () => {
    setModalMode("add");
    setFormData({ title: "", description: "" });
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setCurrentEvent(null);
    setIsModalOpen(true);
  };

  const handleEditEvent = async (event) => {
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 800));
    setModalMode("edit");
    setCurrentEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
    });
    setExistingImages(event.images || []);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setIsModalOpen(true);
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setFormData({ title: "", description: "" });
    setCurrentEvent(null);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter((file) => {
      const isValid = file.type.startsWith("image/");
      if (!isValid) {
        Swal.fire({
          title: "Invalid File",
          text: `${file.name} is not an image file.`,
          icon: "error",
          timer: 2000,
        });
      }
      return isValid;
    });

    setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveNewFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToCloudinary = async (file) => {
    const formDataCloud = new FormData();
    formDataCloud.append("file", file);
    formDataCloud.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formDataCloud.append("cloud_name", CLOUDINARY_CLOUD_NAME);

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        formDataCloud
      );
      return response.data.secure_url;
    } catch (error) {
      console.error("Error uploading to Cloudinary:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Please enter event title.",
        icon: "error",
      });
      return;
    }

    if (!formData.description.trim()) {
      Swal.fire({
        title: "Error!",
        text: "Please enter event description.",
        icon: "error",
      });
      return;
    }

    if (modalMode === "add" && selectedFiles.length === 0) {
      Swal.fire({
        title: "Error!",
        text: "Please select at least one image.",
        icon: "error",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      let uploadedUrls = [...existingImages];

      if (selectedFiles.length > 0) {
        const totalFiles = selectedFiles.length;
        for (let i = 0; i < selectedFiles.length; i++) {
          const url = await uploadToCloudinary(selectedFiles[i]);
          uploadedUrls.push(url);
          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        }
      }

      const data = JSON.stringify({
        title: formData.title,
        description: formData.description,
        images: uploadedUrls,
      });

      const config = {
        method: modalMode === "add" ? "post" : "put",
        maxBodyLength: Infinity,
        url:
          modalMode === "add"
            ? `${process.env.REACT_APP_API_URL}/news/create`
            : `${process.env.REACT_APP_API_URL}/news/update/${currentEvent._id}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: data,
      };

      await axios.request(config);

      Swal.fire({
        title: "Success!",
        text: `Event ${
          modalMode === "add" ? "created" : "updated"
        } successfully.`,
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      handleCloseModal();
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      Swal.fire({
        title: "Error!",
        text: `Something went wrong while ${
          modalMode === "add" ? "creating" : "updating"
        } the event.`,
        icon: "error",
      });
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen p-6 dashboardContainer">
      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-2">
                News & Blogs
              </h1>
            </div>
            <button
              className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg"
              onClick={handleAddEvent}
            >
              <Plus size={20} />
              Add News & Blogs
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <Loader />
          ) : error ? (
            <div className="col-span-full text-center py-16">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
                Error Loading Events
              </h3>
              <p className="text-secondaryTextColor">{error}</p>
            </div>
          ) : (
            events?.map((event) => (
              <div
                key={event?._id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
              >
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 flex gap-2">
                  <button
                    onClick={() => handleEditEvent(event)}
                    className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition-all shadow-md"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(event?._id)}
                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-all shadow-md"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {event?.images && event?.images?.length > 0 && (
                  <div className="aspect-video overflow-hidden relative">
                    {event?.images?.length === 1 ? (
                      <img
                        src={event?.images[0]}
                        alt={event?.title}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/400x300?text=Event+Image";
                        }}
                      />
                    ) : (
                      <Swiper
                        pagination={{
                          clickable: true,
                          dynamicBullets: true,
                        }}
                        autoplay={{
                          delay: 3000,
                          disableOnInteraction: false,
                        }}
                        modules={[Pagination, Autoplay]}
                        className="w-full h-full"
                      >
                        {event?.images?.map((image, index) => (
                          <SwiperSlide key={index}>
                            <img
                              src={image}
                              alt={`${event?.title} - ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    )}
                  </div>
                )}

                <div className="p-4">
                  <h3 className="text-xl font-bold text-primaryTextColor mb-2 line-clamp-2">
                    {event?.title}
                  </h3>
                  <p className="text-secondaryTextColor text-sm line-clamp-3">
                    {event?.description}
                  </p>
                </div>

                <div className="h-1 bg-gradient-to-r from-primaryColor to-secondaryColor transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
              </div>
            ))
          )}
        </div>

        {!isLoading && events.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              No Events Found
            </h3>
            <p className="text-secondaryTextColor mb-6">
              Start by creating your first event
            </p>
            <button
              onClick={handleAddEvent}
              className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300"
            >
              <Plus size={20} />
              Add News & Blogs
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-primaryTextColor">
                {modalMode === "add" ? "Add News & Blog" : "Edit News & Blog"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
                disabled={isUploading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-primaryTextColor mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData?.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor focus:border-transparent"
                  placeholder="Enter title"
                  disabled={isUploading}
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-primaryTextColor mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor focus:border-transparent resize-none"
                  placeholder="Enter description"
                  disabled={isUploading}
                />
              </div>

              {modalMode === "edit" && existingImages.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-primaryTextColor mb-3">
                    Current Images ({existingImages.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {existingImages.map((url, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={url}
                            alt={`Existing ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {!isUploading && (
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-semibold text-primaryTextColor mb-3">
                  {modalMode === "edit" ? "Add More Images" : "Images *"}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primaryColor transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    disabled={isUploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <ImageIcon size={48} className="text-gray-400 mb-3" />
                    <span className="text-primaryColor font-semibold mb-1">
                      Click to upload
                    </span>
                    <span className="text-sm text-gray-500">
                      or drag and drop images here
                    </span>
                    <span className="text-xs text-gray-400 mt-2">
                      PNG, JPG, JPEG, GIF up to 10MB
                    </span>
                  </label>
                </div>
              </div>

              {previewUrls?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-primaryTextColor mb-3">
                    New Images ({selectedFiles?.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {previewUrls?.map((url, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={url}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {!isUploading && (
                          <button
                            type="button"
                            onClick={() => handleRemoveNewFile(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                          >
                            <X size={16} />
                          </button>
                        )}
                        <p className="text-xs text-gray-600 mt-1 truncate">
                          {selectedFiles[index]?.name}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isUploading && (
                <div className="mt-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-primaryTextColor">
                      {modalMode === "add"
                        ? "Creating event..."
                        : "Updating event..."}
                    </span>
                    <span className="text-sm text-gray-600">
                      {uploadProgress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primaryColor h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isUploading}
                className="bg-primaryBtnColor hover:bg-opacity-90 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {modalMode === "add" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    {modalMode === "add" ? "Create" : "Update"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsAndBlog;
